import React, { Component } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Progress, Alert, UncontrolledTooltip } from 'reactstrap'
import { graphql } from '@apollo/client/react/hoc'
import gql from 'graphql-tag'
import { RepsTypeAhead } from '../../TaskManagement/TaskComponents/TypeAheadComponents'
import RepAvatar from '../../Material/RepAvatar'
import withApolloClient from '../../withApolloClient'
import './IncidentAssignmentModal.css'

const UpdateIncidentAssignmentMutation = gql`
mutation UpdateIncidentAssignmentMutation($input: UpdateIncidentAssignmentMutationInput!) {
  updateIncidentAssignment(input: $input) {
    ok
    errors {
      messages
    }
  }
}`

const OpenIncidentTasksQuery = gql`
query OpenIncidentTasksQuery($incidentId: ID!, $filters: [FilterInput]) {
    allTasks(incidentId: $incidentId, filters: $filters) {
        edges {
            node {
                id
                title
                assignedTo {
                    edges {
                        node {
                            id
                            name
                            username
                            firstName
                            lastName
                        }
                    }
                }
            }
        }
    }
}`

const sanitizeDomId = (str) => String(str).replace(/[^a-zA-Z0-9_-]/g, '_')

function AssigneeAvatars({ reps, idPrefix }) {
    const list = reps || []
    if (!list.length) {
        return <RepAvatar rep={null} text="--" keepClose />
    }
    const visible = list.slice(0, 2)
    const overflow = list.slice(2)
    const overflowId = sanitizeDomId(idPrefix)
    return (
        <span className="iam-avatar-group">
            {visible.map((rep) => <RepAvatar key={rep.id} rep={rep} keepClose />)}
            {overflow.length > 0 && (
                <>
                    <span id={overflowId} className="iam-avatar-overflow">+{overflow.length}</span>
                    <UncontrolledTooltip target={overflowId} placement="top">
                        {overflow.map((rep) => (rep.name || rep.username || rep.id)).join(', ')}
                    </UncontrolledTooltip>
                </>
            )}
        </span>
    )
}

export class IncidentAssignmentModal extends Component {
    constructor(props) {
        super(props)
        this.state = {
            value: props.value || [],
            error: null,
            loading: false,
            assignmentCheckedTaskIds: [],
        }
    }

    // Tasks already assigned to exactly the reps currently selected to reassign to
    // are already in the desired state, so they're excluded from the "Open tasks" list.
    getReassignableTasks = () => {
        const tasks = (this.props.assignmentOpenTasksData?.edges || []).map((edge) => edge.node)
        const selectedIds = (this.state.value || []).map((rep) => rep.id).sort()
        if (!selectedIds.length) {
            return tasks
        }
        return tasks.filter((task) => {
            const taskAssigneeIds = (task?.assignedTo?.edges?.map((edge) => edge?.node?.id).filter(Boolean) || []).sort()
            return taskAssigneeIds.length !== selectedIds.length || taskAssigneeIds.some((id, idx) => id !== selectedIds[idx])
        })
    }

    componentDidMount() {
        if (this.props.assignmentOpenTasksData) {
            const tasks = this.getReassignableTasks()
            this.setState({ assignmentCheckedTaskIds: tasks.map((task) => task.id) })
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.value !== this.props.value || prevProps.open !== this.props.open) {
            this.setState({ value: this.props.value || [], error: null })
        }

        if (prevProps.assignmentOpenTasksData !== this.props.assignmentOpenTasksData && this.props.assignmentOpenTasksData) {
            const tasks = this.getReassignableTasks()
            this.setState({ assignmentCheckedTaskIds: tasks.map((task) => task.id) })
        }
    }

    toggleAssignmentTask = (taskId) => {
        this.setState((prevState) => ({
            assignmentCheckedTaskIds: prevState.assignmentCheckedTaskIds.includes(taskId)
                ? prevState.assignmentCheckedTaskIds.filter((id) => id !== taskId)
                : [...prevState.assignmentCheckedTaskIds, taskId],
        }))
    }

    toggleAllAssignmentTasks = () => {
        const tasks = this.getReassignableTasks()
        this.setState((prevState) => {
            const allChecked = tasks.length > 0 && prevState.assignmentCheckedTaskIds.length === tasks.length
            return { assignmentCheckedTaskIds: allChecked ? [] : tasks.map((task) => task.id) }
        })
    }

    confirmAssignment = () => {
        const { apolloClient, incidentId, refetchIncident, handleClose } = this.props
        const { value, assignmentCheckedTaskIds } = this.state
        const reassignableTaskIds = this.getReassignableTasks().map((task) => task.id)

        this.setState({ loading: true, error: null })
        apolloClient.mutate({
            mutation: UpdateIncidentAssignmentMutation,
            variables: {
                input: {
                    incidentId,
                    assignedToIds: (value || []).map((rep) => rep.id),
                    taskIds: assignmentCheckedTaskIds.filter((id) => reassignableTaskIds.includes(id)),
                },
            },
        }).then((response) => {
            if (response.data?.updateIncidentAssignment?.ok) {
                refetchIncident()
                handleClose()
                this.setState({ value: [], loading: false, error: null })
            } else {
                const msg = response.data?.updateIncidentAssignment?.errors?.[0]?.messages.toString() || 'Update failed'
                this.setState({ error: msg, loading: false })
            }
        }).catch(() => {
            this.setState({ loading: false, error: 'An error occurred. Please try again.' })
        })
    }

    render() {
        const { open, handleClose } = this.props
        const { value, loading, error, assignmentCheckedTaskIds } = this.state
        const tasks = this.getReassignableTasks()
        const allOpenTasks = (this.props.assignmentOpenTasksData?.edges || []).map((edge) => edge.node)
        const assignmentTasksLoading = !!this.props.assignmentOpenTasksLoading
        const selectedAssignees = value || []
        const allChecked = tasks.length > 0 && assignmentCheckedTaskIds.length === tasks.length
        const newAssigneeNames = selectedAssignees.map((rep) => rep.name || rep.username).filter(Boolean).join(', ') || 'the selected rep'

        return (
            <Modal isOpen={open} toggle={handleClose} className="incident-assignment-modal">
                <ModalHeader close={<button type="button" className="close" onClick={handleClose} aria-label="Close"><span aria-hidden="true">&times;</span></button>}>Update task assignments</ModalHeader>
                <ModalBody>
                    {error && <Alert color="danger" className="iam-alert">{error}</Alert>}
                    <div className="iam-section">
                        <label className="iam-section-label">Reassign to</label>
                        <RepsTypeAhead onChange={(value) => this.setState({ value })} defaultValue={selectedAssignees} permissions={["custom_can_assign_to_incident"]} returnObjects />
                    </div>

                    {!assignmentTasksLoading && tasks.length === 0 && (
                        <p className="iam-loading">
                            {(allOpenTasks.length > 0 && tasks.length === 0) ? "All open tasks are already assigned to the selected rep." : "There are no open tasks, or you don't have access to the board they're on."}
                        </p>
                    )}
                    {!assignmentTasksLoading && tasks.length > 0 && (
                        <div className="iam-section">
                            <p className="iam-tasks-hint">Select the open tasks to reassign to {newAssigneeNames}.</p>
                            <div className="iam-task-table">
                                <div className="iam-task-row iam-task-row--header">
                                    <label className="iam-task-checkbox">
                                        <input type="checkbox" aria-label="select all" checked={allChecked} onChange={this.toggleAllAssignmentTasks} />
                                    </label>
                                    <span className="iam-col-header">Open task</span>
                                    <span className="iam-col-header">Current assignee</span>
                                    <span className="iam-col-header">New assignee</span>
                                </div>
                                {tasks?.map((task) => (
                                    <label key={task.id} className={`iam-task-row${assignmentCheckedTaskIds.includes(task.id) ? " is-checked" : ""}`}>
                                        <span className="iam-task-checkbox">
                                            <input type="checkbox" aria-label={`include ${task.title}`} checked={assignmentCheckedTaskIds.includes(task.id)} onChange={() => this.toggleAssignmentTask(task.id)} />
                                        </span>
                                        <span className="iam-task-title">{task.title}</span>
                                        <span className="iam-task-cell">
                                            <AssigneeAvatars reps={(task?.assignedTo?.edges?.map((edge) => edge?.node).filter(Boolean) || [])} idPrefix={`iam-current-${task.id}`} />
                                        </span>
                                        <span className="iam-task-cell">
                                            <AssigneeAvatars reps={selectedAssignees} idPrefix={`iam-new-${task.id}`} />
                                        </span>
                                    </label>
                                ))}
                            </div>
                            <p className="iam-tasks-footnote">Unchecked tasks keep their current assignees.</p>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    {loading || assignmentTasksLoading ? (
                        <Progress animated color="info" value={100} className="w-100" />
                    ) : (
                        <>
                            <span className="iam-selected-count">{tasks.length > 0 ? `${assignmentCheckedTaskIds.length} tasks selected` : ''}</span>
                            <Button outline color="secondary" onClick={handleClose}>Cancel</Button>
                            <Button color="primary" onClick={this.confirmAssignment}>Confirm change</Button>
                        </>
                    )}
                </ModalFooter>
            </Modal>
        )
    }
}

export default graphql(OpenIncidentTasksQuery, {
    options: (props) => ({
        variables: {
            incidentId: props.incidentId,
            filters: [{ fieldName: "is_archived", filterType: "equal", filterValues: "false" }, { fieldName: "status", filterType: "in", filterValues: ["OPEN", "IN_PROGRESS"] }],
        },
        fetchPolicy: 'network-only',
    }),
    props: ({ data }) => ({
        assignmentOpenTasksData: data?.allTasks,
        assignmentOpenTasksLoading: data?.loading,
    }),
})(withApolloClient(IncidentAssignmentModal))
